import React, { useState, useRef, useEffect } from 'react';
import { Eye, Check, X, AlertCircle } from 'lucide-react';

export type ValueStatus = 'unverified' | 'verified' | 'rejected' | 'manual';

interface HighlightRange {
  start: number;
  end: number;
  className?: string;
}

interface AIValidatedInputWithHighlightProps {
  value: string;
  initialValueStatus?: ValueStatus;
  valueContext?: string;
  highlightRanges?: HighlightRange[];
  extractionNote?: string; // Optional note explaining how the value was derived
  onChange?: (value: string, status: ValueStatus) => void;
  onStatusChange?: (status: ValueStatus) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  type?: 'text' | 'email' | 'tel' | 'url' | 'number';
}

interface HighlightedTextProps {
  text: string;
  highlights: HighlightRange[];
}

const HighlightedText: React.FC<HighlightedTextProps> = ({ text, highlights }) => {
  if (!highlights || highlights.length === 0) {
    return <span className="whitespace-pre-wrap">{text}</span>;
  }

  // Sort highlights by start position
  const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);
  
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  sortedHighlights.forEach((highlight, index) => {
    // Add text before the highlight
    if (highlight.start > lastIndex) {
      elements.push(
        <span key={`text-${index}`} className="whitespace-pre-wrap">
          {text.slice(lastIndex, highlight.start)}
        </span>
      );
    }

    // Add the highlighted text
    const highlightClass = highlight.className || 'bg-yellow-200 dark:bg-yellow-800 px-1 rounded';
    elements.push(
      <span key={`highlight-${index}`} className={highlightClass}>
        {text.slice(highlight.start, highlight.end)}
      </span>
    );

    lastIndex = highlight.end;
  });

  // Add remaining text after the last highlight
  if (lastIndex < text.length) {
    elements.push(
      <span key="text-end" className="whitespace-pre-wrap">
        {text.slice(lastIndex)}
      </span>
    );
  }

  return <>{elements}</>;
};

interface ContextPopoverProps {
  context: string;
  highlights?: HighlightRange[];
  extractionNote?: string;
  extractedValue: string;
  isVisible: boolean;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const ContextPopover: React.FC<ContextPopoverProps> = ({
  context,
  highlights = [],
  extractionNote,
  extractedValue,
  isVisible,
  onApprove,
  onReject,
  onClose,
  anchorRef
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose, anchorRef]);

  if (!isVisible) return null;

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg"
      style={{
        top: '100%',
        right: 0,
      }}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            AI Extraction Source
          </h4>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Extracted Value Display */}
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
          <div className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1">
            AI Extracted Value:
          </div>
          <div className="text-sm font-mono text-blue-900 dark:text-blue-100 bg-white dark:bg-blue-950 px-2 py-1 rounded border">
            {extractedValue}
          </div>
          {extractionNote && (
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
              {extractionNote}
            </div>
          )}
        </div>

        {/* Source Context */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md max-h-60 overflow-y-auto">
          <div className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-2">
            Source Text:
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <HighlightedText text={context} highlights={highlights} />
          </div>
        </div>
        
        {highlights.length > 0 && (
          <div className="mb-4 text-xs text-gray-600 dark:text-gray-400 flex items-center">
            <div className="w-3 h-3 bg-yellow-200 dark:bg-yellow-800 rounded mr-2"></div>
            Highlighted text shows the source information used by AI
          </div>
        )}
        
        <div className="flex justify-end space-x-2">
          <button
            onClick={onReject}
            className="flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800 rounded-md transition-colors"
          >
            <X className="w-4 h-4 mr-1" />
            Reject
          </button>
          <button
            onClick={onApprove}
            className="flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 rounded-md transition-colors"
          >
            <Check className="w-4 h-4 mr-1" />
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

export const AIValidatedInputWithHighlight: React.FC<AIValidatedInputWithHighlightProps> = ({
  value,
  initialValueStatus = 'manual',
  valueContext,
  highlightRanges = [],
  extractionNote,
  onChange,
  onStatusChange,
  placeholder,
  label,
  disabled = false,
  className = '',
  id,
  name,
  type = 'text'
}) => {
  const [currentStatus, setCurrentStatus] = useState<ValueStatus>(initialValueStatus);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const eyeButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update internal value when prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Update status when prop changes
  useEffect(() => {
    setCurrentStatus(initialValueStatus);
  }, [initialValueStatus]);

  const getInputStyles = () => {
    const baseStyles = 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors';
    
    switch (currentStatus) {
      case 'unverified':
        return `${baseStyles} bg-orange-50 border-orange-200 focus:ring-orange-500 dark:bg-orange-900/20 dark:border-orange-700 dark:focus:ring-orange-400`;
      case 'verified':
        return `${baseStyles} bg-green-50 border-green-200 focus:ring-green-500 dark:bg-green-900/20 dark:border-green-700 dark:focus:ring-green-400`;
      case 'rejected':
        return `${baseStyles} bg-red-50 border-red-200 focus:ring-red-500 dark:bg-red-900/20 dark:border-red-700 dark:focus:ring-red-400`;
      case 'manual':
      default:
        return `${baseStyles} bg-white border-gray-300 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-400 dark:text-gray-100`;
    }
  };

  const getStatusIndicator = () => {
    switch (currentStatus) {
      case 'unverified':
        return (
          <div className="flex items-center text-orange-600 dark:text-orange-400 text-xs mt-1">
            <AlertCircle className="w-3 h-3 mr-1" />
            AI extracted - pending verification
          </div>
        );
      case 'verified':
        return (
          <div className="flex items-center text-green-600 dark:text-green-400 text-xs mt-1">
            <Check className="w-3 h-3 mr-1" />
            AI extracted - verified
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center text-red-600 dark:text-red-400 text-xs mt-1">
            <X className="w-3 h-3 mr-1" />
            AI extraction rejected
          </div>
        );
      default:
        return null;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    
    // If user manually changes the value, mark it as manual
    if (newValue !== value) {
      setCurrentStatus('manual');
      onStatusChange?.('manual');
    }
    
    onChange?.(newValue, currentStatus);
  };

  const handleApprove = () => {
    setCurrentStatus('verified');
    setIsPopoverVisible(false);
    onStatusChange?.('verified');
    onChange?.(internalValue, 'verified');
  };

  const handleReject = () => {
    setCurrentStatus('rejected');
    setIsPopoverVisible(false);
    onStatusChange?.('rejected');
    onChange?.(internalValue, 'rejected');
  };

  const handleEyeClick = () => {
    setIsPopoverVisible(!isPopoverVisible);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type={type}
          id={id}
          name={name}
          value={internalValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={getInputStyles()}
        />
        
        {valueContext && (
          <button
            ref={eyeButtonRef}
            type="button"
            onClick={handleEyeClick}
            className="absolute -top-2 -right-2 p-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            title="View extraction context"
          >
            <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
        )}
        
        {valueContext && (
          <ContextPopover
            context={valueContext}
            highlights={highlightRanges}
            extractionNote={extractionNote}
            extractedValue={internalValue}
            isVisible={isPopoverVisible}
            onApprove={handleApprove}
            onReject={handleReject}
            onClose={() => setIsPopoverVisible(false)}
            anchorRef={eyeButtonRef}
          />
        )}
      </div>
      
      {getStatusIndicator()}
    </div>
  );
}; 